// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2022 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { ProductBrandStyles } from "@insite/content-library/Components/ProductBrand";
import { ProductDescriptionStyles } from "@insite/content-library/Components/ProductDescription";
import { ProductImageStyles } from "@insite/content-library/Components/ProductImage";
import { ProductPartNumbersStyles } from "@insite/content-library/Components/ProductPartNumbers";
import { ProductPriceStyles } from "@insite/content-library/Components/ProductPrice";
import { SmallHeadingAndTextStyles } from "@insite/content-library/Components/SmallHeadingAndText";
import { ButtonPresentationProps } from "@insite/mobius/Button";
import { GridItemProps } from "@insite/mobius/GridItem";
import { IconWrapper } from "@insite/mobius/Icon";
import { IconPresentationProps } from "@insite/mobius/Icon";
import { LinkPresentationProps } from "@insite/mobius/Link";
import { TypographyPresentationProps, TypographyProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { cartLineBreakpoints } from "@plumberssupply/App/Components/Cart/CartContainerLayout";
import { RoundToBoxStyles } from "@plumberssupply/App/Components/Product/RoundToBox";
import {
    ProductAvailabilityStyles,
    productAvailabilityStyles,
} from "@plumberssupply/Overrides/Components/ProductAvailability";
import AlertWarning from "@plumberssupply/XDL/Icons/AlertWarning";
import IconList from "@plumberssupply/XDL/Icons/IconList";
import {
    color,
    createLinkIconStyles,
    ellipsis,
    iconLinkCommonStyles,
    iconStyles,
    mergeWithCss,
    xScreen,
} from "@plumberssupply/XDL/Utilities";
import { css } from "styled-components";

export interface CartLineSharedStyles {
    header?: {
        wrapper?: InjectableCss;
        title?: TypographyPresentationProps;
    };
    productImage?: ProductImageStyles;
    productBrand?: ProductBrandStyles;
    productDescription?: ProductDescriptionStyles;
    productPartNumbers?: ProductPartNumbersStyles;
    addToListLink?: LinkPresentationProps;
    removeCartLineLink?: LinkPresentationProps;
    quantityHeadingAndText?: SmallHeadingAndTextStyles;
    productPrice?: ProductPriceStyles;
    costCodeGridItem?: GridItemProps;
    costCodeLabelText?: TypographyPresentationProps;
    costCodeText?: TypographyPresentationProps;
    readOnlyNotes?: SmallHeadingAndTextStyles;
    priceHeadingAndText?: SmallHeadingAndTextStyles;
    promotionsContainer?: InjectableCss;
    promotionNameLabel?: TypographyPresentationProps;
    promotionNameText?: TypographyPresentationProps;
    extendedUnitNetPriceText?: TypographyPresentationProps;
    productBrandAndDescriptionGridItem?: InjectableCss;
    productPartNumbersGridItem?: InjectableCss;
    quantityRoundToBox?: InjectableCss;
    roundToBox?: RoundToBoxStyles;
    addLineNotes?: LinkPresentationProps;
    productAvailability?: ProductAvailabilityStyles;
    promotionsAndScheduleWrapper?: InjectableCss;
    configurationOptionsWrapper?: InjectableCss;
    configurationOptionText?: TypographyPresentationProps;
    configurationOptionLabelText?: TypographyPresentationProps;
    deliveryScheduleButton?: ButtonPresentationProps;
    productInfoTitle?: SmallHeadingAndTextStyles;
    qtyOrderedAndShipped?: TypographyProps;
    warningIcon?: IconPresentationProps;
    productInfoWrappers?: InjectableCss;
}

const cartLineSharedStyles: CartLineSharedStyles = {
    productImage: {
        linkWrappingImage: {
            css: css`
                width: 88px;
                height: 88px;
            `,
        },
        img: {
            css: css`
                max-width: 100%;
                max-height: 100%;
                height: auto;
                object-fit: contain;
            `,
        },
    },
    productInfoWrappers: {
        css: css``,
    },
    productBrandAndDescriptionGridItem: {
        css: css`
            min-width: 100%;
            display: flex;
            flex-direction: column;
            padding-right: 20px;
            margin-bottom: 8px;

            ${xScreen("lte", "md")} {
                margin: 0;
                padding: 0;
            }
        `,
    },
    productPartNumbersGridItem: {
        css: css`
            display: flex;
            column-gap: 12px;
            ${xScreen("lt", cartLineBreakpoints.md)} {
                flex-wrap: wrap;
                row-gap: 8px;
            }
        `,
    },
    productDescription: {
        productDetailLink: {
            css: css`
                display: block;
                width: auto;
                align-self: baseline;
                ${ellipsis(3)}
                ${xScreen("lte", "md")} {
                    min-height: unset;
                }
            `,
            typographyProps: {
                css: css`
                    ${ellipsis(3)};
                    ${xScreen("lt", "sm")} {
                        ${ellipsis(5)};
                    }
                `,
            },
        },
    },
    quantityRoundToBox: {
        css: css`
            padding-top: 16px;
        `,
    },
    roundToBox: {
        checkbox: {
            iconProps: {
                size: 12,
            },
            typographyProps: {
                css: css`
                    &&& {
                        display: flex;
                        align-items: center;
                    }
                `,
            },
            css: css`
                justify-content: flex-start;
            `,
        },
    },
    addLineNotes: {
        typographyProps: {
            size: 12,
        },
        css: css`
            margin-right: 68px;
            ${xScreen("gte", cartLineBreakpoints.md)} {
                margin-right: 0;
                max-width: 177px;
                width: 177px;
            }
            ${xScreen("lte", "xs")} {
                margin-right: 36px;
            }
            ${IconWrapper} {
                margin-left: 4px;
                color: ${color("secondary")};
            }

            &:hover {
                ${IconWrapper} {
                    color: ${color("common.ui1")};
                }
            }
        `,
    },
    addToListLink: {
        ...mergeWithCss(iconLinkCommonStyles(IconList), { ...iconStyles }),
    },
    productAvailability: {
        container: {
            css: css`
                ${productAvailabilityStyles?.container?.css}
                justify-content: center;
                flex-grow: 1;
                ${xScreen("lte", "sm")} {
                    flex-grow: initial;
                    justify-content: flex-start;
                }
                ${xScreen("lte", "md")} {
                    margin-bottom: 16px;
                }
            `,
        },
    },
    removeCartLineLink: {
        ...createLinkIconStyles(
            "X",
            css`
                line-height: 20px;
            `,
            css`
                &&&& {
                    opacity: 1;
                    color: ${color("common.ui8")};
                    width: 18px;
                    > svg {
                        width: 18px;
                        height: 18px;
                    }
                    &:hover {
                        color: ${color("danger")};
                    }
                }
            `,
        ),
        css: css`
            margin-left: auto;
            ${xScreen("gt", "md")} {
                position: absolute;
                top: 28px;
                right: 0;
            }
        `,
    },
    extendedUnitNetPriceText: {
        weight: 800,
        size: 20,
        lineHeight: "22px",
        color: "common.ui11",
    },
    productPrice: {
        wrapper: {
            css: css`
                ${xScreen("lte", "md")} {
                    margin-bottom: 9px;
                    & > div {
                        line-height: 18px;
                    }
                }
            `,
        },
        price: {
            priceText: {
                weight: 800,
                size: 18,
                lineHeight: "22px",
                color: "common.ui11",
                css: css`
                    ${xScreen("lte", "md")} {
                        font-size: 14px;
                        line-height: 18px;
                    }
                `,
            },
            unitOfMeasure: {
                unitOfMeasureText: {
                    css: css`
                        ${xScreen("lte", "md")} {
                            font-size: 12px;
                        }
                    `,
                },
            },
        },
    },
    promotionsAndScheduleWrapper: {
        css: css`
            display: flex;
            flex-direction: column;
        `,
    },
    configurationOptionsWrapper: {
        css: css`
            display: flex;
            flex-direction: column;
        `,
    },
    configurationOptionLabelText: {
        size: 12,
        weight: "bold",
        css: css`
            &:after {
                content: ": ";
            }
        `,
    },
    configurationOptionText: {
        size: 12,
    },
    promotionNameText: {
        size: 12,
    },
    costCodeLabelText: {
        size: 12,
        weight: "bold",
    },
    costCodeText: {
        size: 12,
    },
    deliveryScheduleButton: {
        css: css`
            margin-top: 15px;
        `,
    },
    qtyOrderedAndShipped: {
        size: 12,
        lineHeight: "13px",
        css: css`
            display: flex;
            align-items: end;
            margin-top: 20px;
            ${xScreen("gt", cartLineBreakpoints.md)} {
                margin-top: 22px;
            }
        `,
    },
    warningIcon: {
        src: AlertWarning,
        size: 16,
        color: "#C0AB1B",
        css: css`
            margin-right: 8px;
            line-height: 16px;
        `,
    },
};
cartLineSharedStyles.priceHeadingAndText = {
    ...mergeWithCss(cartLineSharedStyles.extendedUnitNetPriceText, {
        extendedStyles: {
            wrapper: {
                css: css`
                    display: flex;
                    justify-content: flex-start;
                    align-items: center;
                    flex-direction: column;
                `,
            },
        },
    }),
};
cartLineSharedStyles.quantityHeadingAndText = {
    ...mergeWithCss(cartLineSharedStyles.extendedUnitNetPriceText, {
        extendedStyles: {
            wrapper: {
                css: css`
                    display: flex;
                    justify-content: flex-start;
                    align-items: flex-start;
                    flex-direction: column;
                `,
            },
        },
    } as SmallHeadingAndTextStyles),
};
export default cartLineSharedStyles;
